import { useEffect, useRef } from 'react';
import * as Sentry from '@sentry/browser';

interface RememberedFields {
    bookingMadeBy: string;
    dentistNameAndPractiseName: string;
    email: string;
    region: string;
}

export function useRememberFieldsWithLocalStorage(
    form: { values: RememberedFields; setValues: (values: Partial<RememberedFields>) => void },
    isAdmin: boolean,
    isLoaded: boolean,
    sessionId: string | null | undefined) {

    const previousSession = useRef<string | null | undefined>(undefined);
    useEffect(() => {
        if (!isLoaded) return;
        const currentSession = isAdmin ? sessionId : null;
        if (previousSession.current === undefined) {
            previousSession.current = currentSession;
            const storedValue = window.localStorage.getItem('oralart-prebooking-form');
            if (storedValue) {
                try {
                    const stored = JSON.parse(storedValue);
                    form.setValues({
                        bookingMadeBy: stored.bookingMadeBy ?? '',
                        ...(!isAdmin && {
                            dentistNameAndPractiseName: stored.dentistNameAndPractiseName ?? '',
                            email: stored.email ?? '',
                            region: stored.region ?? '',
                        }),
                    });
                } catch (error) {
                    Sentry.captureException(error);
                }
            }
            if (!isAdmin) return;
        } else if (previousSession.current !== currentSession) {
            previousSession.current = currentSession;
            form.setValues({ dentistNameAndPractiseName: '', email: '' });
            window.localStorage.setItem('oralart-prebooking-form', JSON.stringify({
                bookingMadeBy: form.values.bookingMadeBy,
            }));
            return;
        }

        window.localStorage.setItem('oralart-prebooking-form', JSON.stringify({
            bookingMadeBy: form.values.bookingMadeBy,
            ...(!isAdmin && {
                dentistNameAndPractiseName: form.values.dentistNameAndPractiseName,
                email: form.values.email,
                region: form.values.region,
            }),
        }));
    }, [form.values, isAdmin, isLoaded, sessionId]);
}
